import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Activity } from '@models/activity.model';
import { activityService } from '@services/activity';
import { PaginationActions } from '../index';

interface UseProjectFeedByProjectsProps {
  projectsId: string[];
  types?: string[];
  // Optional server-side filter on Activity.resolutionStatus (e.g. 'ACTIVE'), used by the
  // "Pending Invoices" tab to list only unresolved INVOICE_PENDING activities.
  resolutionStatus?: string;
}

const LIMIT = 10;

/**
 * Activity feed for a set of projects (dashboard + projects pages), paginated by
 * keyset cursor. Because keyset pagination never overlaps or skips rows under
 * live inserts, accumulation is a plain flatMap over the fetched pages — no
 * manual page counter, dedup, or signature tracking required.
 */
export const useProjectFeedByProjects = ({
  projectsId,
  types,
  resolutionStatus,
}: UseProjectFeedByProjectsProps) => {
  const isEnabled = Boolean(projectsId && projectsId.length > 0 && projectsId[0]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch: refetchActivities,
  } = useInfiniteQuery({
    // types is part of the key, so changing the triage filter resets the feed
    // to its first page automatically (React Query handles the reset).
    queryKey: [
      'activities',
      'byProjects',
      projectsId?.slice().sort(),
      { types: types?.slice().sort(), limit: LIMIT, resolutionStatus },
    ],
    enabled: isEnabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      activityService.getActivitiesByProjects(
        projectsId,
        undefined,
        LIMIT,
        types,
        pageParam as string | undefined,
        resolutionStatus,
      ),
    getNextPageParam: (lastPage) =>
      lastPage?.hasMore ? lastPage?.nextCursor ?? undefined : undefined,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    // Auto-refresh the feed every 60s. On an infinite query this refetches all
    // currently-loaded pages in place; keyset pagination keeps them consistent,
    // so new activities surface at the top without a scroll jump. Pauses while
    // the tab is backgrounded (refetchIntervalInBackground defaults to false).
    refetchInterval: 60 * 1000,
  });

  const activities = useMemo<Activity[]>(
    () => data?.pages?.flatMap((page) => page.items) ?? [],
    [data],
  );

  const totalCount = useMemo(() => data?.pages?.[0]?.total ?? 0, [data]);

  const loadMore = useCallback(() => {
    if (isLoading || isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  const paginationActions: PaginationActions = {
    loadMore,
    hasMore: Boolean(hasNextPage),
    isLoading: isLoading && activities.length === 0,
    isFetchingMore: isFetchingNextPage,
  };

  return {
    activities,
    paginationActions,
    totalCount,
    refetchActivities,
  };
};
